import { useState } from 'react';
import Flow, { nodeAt } from './engine/flow.js';
import Step from './steps/index.js';

/*
 * Tree
 *
 * The one stateful thing in the app. It holds the engine's state, hands the
 * current node to a renderer, and puts whatever comes back through a Flow
 * transition. Every rule about where the customer goes next lives in the
 * engine; nothing about it is decided here.
 *
 * That split is what made P2's tests possible, and it is worth keeping: the
 * moment a renderer starts deciding a destination, the graph stops being
 * checkable as data.
 *
 * IT DOES NOT OWN THE PAGE AROUND IT. The Support Center wraps this in our
 * chrome; a dealership's replicated website wraps it in theirs. So the frame comes
 * in as `chrome`, a function given the actions it needs to draw and the step to
 * put inside. Nothing about the flow changes between the two, and neither app
 * has to accept the other's header.
 *
 * `resolveBrand` maps a brand id to a brand, because a handoff moves the
 * customer into another tree. A site that only carries OmniShield can leave it
 * out; a handoff to a brand it does not know simply does not happen.
 *
 * THE BRAND COMES FROM THE STATE, NOT THE PROP. A handoff moves the customer
 * from Applied Fire into another brand's tree, and everything from that point
 *, the tree, the accent, the escalation policy, has to follow them across.
 */
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
const Tree = ({
  brand: entryBrand,
  chrome,
  entry,
  notice,
  onRestart,
  resolveBrand
}) => {
  const [state, setState] = useState(() => Flow.start(entryBrand, {
    entry: entry
  }));

  // Once the escalation is filed, Back would remount SubmitStep and file a
  // second one.
  const [sent, setSent] = useState(false);
  const brand = resolveBrand && resolveBrand(state.brandId) || entryBrand;
  const node = nodeAt(state, brand);
  const onAnswer = value => setState(Flow.answer(state, brand, value));
  const onBack = () => setState(Flow.back(state, resolveBrand || (() => brand)));
  const onEscalate = (reason, channel) => setState(Flow.escalate(state, brand, reason, channel));
  const onHandoff = brandId => {
    let next = resolveBrand && resolveBrand(brandId);
    if (next) {
      setState(Flow.handoff(state, next));
    }
  };
  const onLookup = result => setState(Flow.applyLookup(state, brand, result));
  const onShown = id => setState(current => Flow.markDocumentShown(current, id));
  const actions = {
    brand: brand,
    canGoBack: !sent && Flow.canGoBack(state),
    onBack: onBack,
    onCall: onEscalate.bind(this, 'general', 'call'),
    onContact: state.phase === 'tree' ? onEscalate.bind(this, 'general', null) : null,
    onRestart: onRestart
  };
  if (!node) {
    return chrome({
      ...actions,
      children: null,
      missing: true
    });
  }
  return chrome({
    ...actions,
    children: /*#__PURE__*/_jsxs(_Fragment, {
      children: [state.history.length === 0 && notice, /*#__PURE__*/_jsx(Step, {
        answers: state.answers,
        brand: brand,
        node: node,
        onContinue: node?.continue ? onAnswer.bind(this, node.continue) : onAnswer,
        onEscalate: onEscalate,
        onHandoff: onHandoff,
        onResult: onLookup,
        onSent: () => setSent(true),
        onSelect: onAnswer,
        options: Flow.options(state, brand),
        lookup: state.lookup,
        onShown: onShown,
        onSubmit: onAnswer,
        nodeId: state.nodeId,
        payload: Flow.isComplete(state, brand) || node?.type === 'assistant' ? Flow.payload(state, brand) : null,
        stepKey: `${state.phase}:${state.nodeId}`,
        target: node?.type === 'handoff' ? resolveBrand && resolveBrand(node.brand) : null
      })]
    })
  });
};
export default Tree;